// Authentication utilities
export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const USER_STORAGE_KEY = 'user_data';

/**
 * Store authentication tokens securely
 * @param {Object} tokens - Object containing accessToken and refreshToken
 */
export const setAuthTokens = (tokens) => {
  if (tokens && tokens.accessToken && tokens.refreshToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

/**
 * Store authentication token securely (legacy support)
 * @param {string} token - JWT token
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }
};

/**
 * Get access token
 * @returns {string|null} Access token or null
 */
export const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Get refresh token
 * @returns {string|null} Refresh token or null
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Get authentication token (legacy support)
 * @returns {string|null} JWT token or null
 */
export const getAuthToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Store user data
 * @param {Object} userData - User information
 */
export const setUserData = (userData) => {
  if (userData) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
  } else {
    localStorage.removeItem(USER_STORAGE_KEY);
  }
};

/**
 * Get user data
 * @returns {Object|null} User data or null
 */
export const getUserData = () => {
  try {
    const userData = localStorage.getItem(USER_STORAGE_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const isAuthenticated = () => {
  const accessToken = getAccessToken();
  const userData = getUserData();
  return !!(accessToken && userData);
};

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  // Also clear legacy keys for backward compatibility
  localStorage.removeItem('auth_token');
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('currentUser');
};

/**
 * Get authorization headers for API requests
 * @returns {Object} Headers object
 */
export const getAuthHeaders = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
